import { Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { agreementPeriod, kr, type AgreementPeriod } from '@kff/domain';
import type { Api } from '../api.js';
import { HandoverDialog, handoverToast } from './handover.js';
import { dm, dmSv } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import { Avatar, Block, LoadError, Loading, PageHead, toast } from '../ui.js';
import type { AgreementRow, Options } from './Agreements.js';
import { agreementLine, type AgreementFact } from './Clients.js';

/**
 * Agreements (owner 2026-10-27): the signed client agreements on their own menu item, read only, so a producer sees what a
 * client has bought and when it runs out without going through the sales pages. Built from the quotes: a signed quote is
 * the agreement; the newest signed one per client is in force, older ones and cancelled ones are ended.
 * Beside them, the agreements as Monday had them (0050), until the clients sign in the Hub (owner 2026-10-31): a Hub
 * agreement in force replaces the client's Monday one.
 */

type Filter = 'active' | 'renew' | 'ended';

/** GET /api/agreements/monday: an agreement as Monday's All Client Data had it, with its files in Drive */
export interface MondayAgreementRow {
  client: { id: string; name: string; code: string };
  lifecycle: string;
  contract: { package: string | null; mrr: number | null; value: number | null; terminationMonths: number | null; start: string | null; end: string | null } | null;
  files: { id: string; name: string; at: string | null; url: string | null; state: 'copied' | 'waiting' | 'failed' }[];
  state: 'active' | 'ended';
  renewSoon: boolean;
}
type Item = { kind: 'hub'; id: string; name: string; c: ContractRow } | { kind: 'monday'; id: string; name: string; m: MondayAgreementRow; state: 'active' | 'ended'; superseded: boolean };

/** GET /api/signing/open: quotes and model releases sent for e-signing and not finished yet. */
export interface OpenSigning {
  id: string;
  kind: 'agreement' | 'release';
  title: string;
  link: string;
  sentAt: string;
  sentBy: string | null;
  expiresAt: string;
  done: number;
  total: number;
  waitingOn: { name: string; viewed: boolean }[];
}

const TX = texts(
  {
    title: 'Agreements',
    sub: 'Signed client agreements: what is included and when they run out.',
    filters: { active: 'In force', renew: 'Renew soon', ended: 'Ended' } as Record<Filter, string>,
    cols: { client: 'Client', agreement: 'Agreement', period: 'Period', resp: 'Responsible' },
    subscription: 'Subscription',
    perProduction: 'Per production',
    perMonth: '/month',
    perProd: 'per production',
    perHour: '/h',
    from: 'from ',
    superseded: 'Replaced by a newer agreement',
    cancelled: 'Cancelled',
    none: (f: string) => `No agreements under ${f}`,
    noneHint: 'An agreement shows here when its quote is marked signed, or when Monday has one for the client.',
    fromMonday: 'From Monday',
    mondayTitle: 'Agreement in Monday',
    mondayNote: 'As Monday had it. An agreement signed in the Hub replaces it.',
    mondayPkg: 'Package',
    mondayMrr: 'Per month',
    mondayValue: 'Agreement value',
    mondayNotice: 'Notice',
    noticeMonths: (m: number) => `${m} ${m === 1 ? 'month' : 'months'}`,
    runsOut: (d: string) => `Runs out ${d}`,
    files: 'Agreement files',
    noFiles: 'No agreement files in Monday.',
    fileWaiting: 'being copied to Drive',
    fileFailed: 'could not be copied, open it in Monday',
    pick: 'Pick an agreement to see what it includes.',
    agreement: (r: string) => `Agreement ${r}`,
    included: 'Included',
    callOff: 'Packages to call off',
    frame: 'Frame agreement with no minimum',
    minimum: (n: number, m: number) => `At least ${n} ${n === 1 ? 'production' : 'productions'} within ${m} ${m === 1 ? 'month' : 'months'}`,
    binding: (m: number) => `${m} months minimum term`,
    untilNotice: 'Runs until notice',
    options: 'Options',
    contents: 'Custom contents',
    special: 'Special terms',
    dates: 'Dates',
    start: 'Start',
    end: 'Runs to',
    noticeBy: 'Notice by',
    signed: 'Signed',
    open: 'Open the agreement',
    client: 'Client card',
    handover: 'Handover',
    handedTo: (n: string, d: string) => `Handed over to ${n} ${d}`,
    readSummary: 'Read the summary on the client card',
    notHanded: 'Not handed over to a producer yet.',
    handOver: 'Hand over to a producer',
    projects: 'Projects under the agreement',
    calledOff: (n: number, min: number) => `${n} of at least ${min} called off`,
    noProjects: 'No projects in the period yet.',
    signedPdf: 'Signed agreement (PDF)',
    noPdf: 'No signed PDF yet. Upload the PDF the client signed, or send the quote for signing in the Hub.',
    out: 'Out for signing',
    kinds: { agreement: 'Quote', release: 'Model release' } as Record<OpenSigning['kind'], string>,
    sentBy: (n: string, d: string) => `Sent by ${n} ${d}`,
    sentOn: (d: string) => `Sent ${d}`,
    signedOf: (n: number, m: number) => `${n} of ${m} signed`,
    waiting: (n: string) => `Waiting on ${n}`,
    viewed: 'opened',
    openPdf: 'Open the signed PDF',
    uploadPdf: 'Upload signed PDF',
    replacePdf: 'Replace PDF',
    uploading: 'Uploading…',
    uploaded: 'The signed PDF is saved'
  },
  {
    title: 'Avtal',
    sub: 'Signerade kundavtal: vad som ingår och när de löper ut.',
    filters: { active: 'Gällande', renew: 'Förnyas snart', ended: 'Avslutade' },
    cols: { client: 'Kund', agreement: 'Avtal', period: 'Period', resp: 'Ansvarig' },
    subscription: 'Abonnemang',
    perProduction: 'Per produktion',
    perMonth: '/mån',
    perProd: 'per produktion',
    perHour: '/tim',
    from: 'från ',
    superseded: 'Ersatt av ett nyare avtal',
    cancelled: 'Avbrutet',
    none: (f: string) => `Inga avtal under ${f}`,
    noneHint: 'Ett avtal hamnar här när dess offert markeras som signerad, eller när Monday har ett för kunden.',
    fromMonday: 'Från Monday',
    mondayTitle: 'Avtal i Monday',
    mondayNote: 'Som det stod i Monday. Ett avtal som signeras i Hubben ersätter det.',
    mondayPkg: 'Paket',
    mondayMrr: 'Per månad',
    mondayValue: 'Avtalsvärde',
    mondayNotice: 'Uppsägning',
    noticeMonths: (m: number) => `${m} ${m === 1 ? 'månad' : 'månader'}`,
    runsOut: (d: string) => `Löper ut ${d}`,
    files: 'Avtalsfiler',
    noFiles: 'Inga avtalsfiler i Monday.',
    fileWaiting: 'kopieras till Drive',
    fileFailed: 'kunde inte kopieras, öppna i Monday',
    pick: 'Välj ett avtal för att se vad som ingår.',
    agreement: (r: string) => `Avtal ${r}`,
    included: 'Det här ingår',
    callOff: 'Paket att avropa',
    frame: 'Ramavtal utan minimum',
    minimum: (n: number, m: number) => `Minst ${n} ${n === 1 ? 'produktion' : 'produktioner'} inom ${m} ${m === 1 ? 'månad' : 'månader'}`,
    binding: (m: number) => `${m} månaders bindningstid`,
    untilNotice: 'Löper tills vidare',
    options: 'Tillval',
    contents: 'Innehåll',
    special: 'Särskilda villkor',
    dates: 'Datum',
    start: 'Start',
    end: 'Löper till',
    noticeBy: 'Säg upp senast',
    signed: 'Signerat',
    open: 'Öppna avtalet',
    client: 'Kundkort',
    handover: 'Överlämning',
    handedTo: (n: string, d: string) => `Överlämnad till ${n} ${d}`,
    readSummary: 'Läs sammanfattningen på kundkortet',
    notHanded: 'Inte överlämnad till en producent än.',
    handOver: 'Lämna över till producent',
    projects: 'Projekt under avtalet',
    calledOff: (n: number, min: number) => `${n} av minst ${min} avropade`,
    noProjects: 'Inga projekt i perioden än.',
    signedPdf: 'Signerat avtal (PDF)',
    noPdf: 'Ingen signerad PDF än. Ladda upp PDF:en som kunden signerade, eller skicka offerten för signering i Hubben.',
    out: 'Ute för signering',
    kinds: { agreement: 'Offert', release: 'Modellavtal' },
    sentBy: (n: string, d: string) => `Skickat av ${n} ${d}`,
    sentOn: (d: string) => `Skickat ${d}`,
    signedOf: (n: number, m: number) => `${n} av ${m} har signerat`,
    waiting: (n: string) => `Väntar på ${n}`,
    viewed: 'öppnat',
    openPdf: 'Öppna signerad PDF',
    uploadPdf: 'Ladda upp signerad PDF',
    replacePdf: 'Byt PDF',
    uploading: 'Laddar upp…',
    uploaded: 'Den signerade PDF:en är sparad'
  }
);

const FILTERS: Filter[] = ['active', 'renew', 'ended'];

export interface ContractRow {
  a: AgreementRow;
  state: 'active' | 'ended';
  /** Why an ended one ended */
  endedBy: 'superseded' | 'cancelled' | null;
  period: AgreementPeriod | null;
}

const startOf = (a: AgreementRow) => a.terms.startDate ?? a.signedOn ?? '';

/** Signed quotes as agreements: the newest signed per client is in force (as on the client card), the rest ended. */
export function contractsOf(rows: AgreementRow[], today: string): ContractRow[] {
  const newest = new Map<string, string>();
  for (const a of rows.filter((x) => x.status === 'signed').sort((x, y) => startOf(y).localeCompare(startOf(x)))) {
    const k = a.client?.id ?? `p:${a.name}`;
    if (!newest.has(k)) newest.set(k, a.id);
  }
  return rows
    .filter((a) => a.status === 'signed' || (a.status === 'cancelled' && a.signedOn))
    .map((a) => {
      const inForce = a.status === 'signed' && newest.get(a.client?.id ?? `p:${a.name}`) === a.id;
      return {
        a,
        state: inForce ? ('active' as const) : ('ended' as const),
        endedBy: inForce ? null : a.status === 'cancelled' ? ('cancelled' as const) : ('superseded' as const),
        period: agreementPeriod(a.terms, a.signedOn, today)
      };
    });
}

const inFilter = (c: ContractRow, f: Filter) => (f === 'ended' ? c.state === 'ended' : c.state === 'active' && (f === 'active' || Boolean(c.period?.renewSoon)));

/** The Hub's agreements and Monday's on one list; a client's Hub agreement in force ends its Monday one. */
export function itemsOf(contracts: ContractRow[], monday: MondayAgreementRow[]): Item[] {
  const hubActive = new Set(contracts.filter((c) => c.state === 'active' && c.a.client).map((c) => c.a.client!.id));
  return [
    ...contracts.map((c) => ({ kind: 'hub' as const, id: c.a.id, name: c.a.name, c })),
    ...monday.map((m) => {
      const superseded = hubActive.has(m.client.id);
      return { kind: 'monday' as const, id: `monday:${m.client.id}`, name: m.client.name, m, state: superseded ? ('ended' as const) : m.state, superseded };
    })
  ];
}

const itemIn = (x: Item, f: Filter) =>
  x.kind === 'hub' ? inFilter(x.c, f) : f === 'ended' ? x.state === 'ended' : x.state === 'active' && (f === 'active' || x.m.renewSoon);

const mondayWhat = (m: MondayAgreementRow, lang: Lang) =>
  [m.contract?.package, m.contract?.mrr ? `${kr(m.contract.mrr)}${TX[lang].perMonth}` : null].filter(Boolean).join(' · ') || '–';

/** The client page's agreement line wants its fact shape. */
export function contractFact(c: ContractRow): AgreementFact {
  const a = c.a;
  return {
    id: a.id,
    ref: a.ref,
    title: a.title,
    state: c.state,
    kind: a.kind,
    packageKey: a.terms.packageKey,
    packageLevel: null,
    callOffKeys: a.kind === 'project' ? (a.terms.callOffKeys ?? [a.terms.packageKey]) : [],
    minProductions: a.terms.minProductions,
    monthly: a.summary.monthly,
    options: [],
    signedOn: a.signedOn,
    sentAt: a.sentAt,
    validUntil: a.validUntil,
    endedOn: c.state === 'ended' ? (a.updatedAt ?? a.createdAt).slice(0, 10) : null,
    period: c.period
  };
}

/** "Spielberg · 45 000 kr/mån" or "Bergman, Spielberg · min 2" */
export const contractWhat = (a: AgreementRow, lang: Lang) =>
  a.kind === 'subscription' ? `${a.terms.packageKey} · ${kr(a.summary.monthly)}${TX[lang].perMonth}` : `${(a.terms.callOffKeys ?? [a.terms.packageKey]).join(', ')}${a.terms.minProductions ? ` · min ${a.terms.minProductions}` : ''}`;

const today0 = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date());
const dayUi = (k: string, lang: Lang) => `${lang === 'sv' ? dmSv(k) : dm(k)} ${k.slice(0, 4)}`;

export function ContractsPage(p: { api: Api }) {
  const lang = useLang();
  const t = TX[lang];
  const [rows, setRows] = useState<AgreementRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [filter, setFilter] = useState<Filter>('active');
  const [openId, setOpenId] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [producers, setProducers] = useState<{ id: string; name: string }[]>([]);
  const [signing, setSigning] = useState<OpenSigning[]>([]);
  const [monday, setMonday] = useState<MondayAgreementRow[] | null>(null);
  useEffect(() => {
    p.api.get<{ agreements: MondayAgreementRow[] }>('/api/agreements/monday').then((r) => setMonday(r.agreements ?? []), () => setMonday([]));
  }, []);
  useEffect(() => {
    p.api.get<{ open: OpenSigning[] }>('/api/signing/open').then((r) => setSigning(r.open ?? []), () => {});
  }, []);
  const load = () =>
    p.api.get<{ agreements: AgreementRow[]; canEdit?: boolean }>('/api/agreements').then((r) => {
      setRows(r.agreements);
      setCanEdit(Boolean(r.canEdit));
    }, setError);
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (canEdit && !producers.length) p.api.get<Options>('/api/agreements/options').then((o) => setProducers(o.producers ?? []), () => {});
  }, [canEdit]);
  if (error) return <LoadError error={error} />;
  if (!rows || !monday) return <Loading />;
  const today = today0();
  const all = itemsOf(contractsOf(rows, today), monday);
  const shown = all.filter((x) => itemIn(x, filter)).sort((x, y) => x.name.localeCompare(y.name, 'sv') || (x.kind === 'hub' ? -1 : 1));
  const open = shown.find((x) => x.id === openId) ?? null;

  return (
    <div class="screen">
      <PageHead title={t.title} sub={t.sub} />
      <nav class="seg" aria-label={t.title} data-tour="contract-filters">
        {FILTERS.map((f) => (
          <button type="button" key={f} class={`seg-btn${f === filter ? ' is-on' : ''}`} aria-pressed={f === filter} onClick={() => setFilter(f)}>
            {t.filters[f]} <span class="muted">{all.filter((x) => itemIn(x, f)).length}</span>
          </button>
        ))}
      </nav>
      {signing.length ? <OutForSigning rows={signing} lang={lang} /> : null}
      {shown.length ? (
        <div class="contracts">
          <div class="tbl-wrap panel" tabIndex={0} role="region" aria-label={t.title} data-tour="contracts-table">
            <table class="tbl">
              <thead>
                <tr>
                  <th scope="col">{t.cols.client}</th>
                  <th scope="col">{t.cols.agreement}</th>
                  <th scope="col">{t.cols.period}</th>
                  <th scope="col">{t.cols.resp}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((x) => {
                  if (x.kind === 'monday') return <MondayRow key={x.id} x={x} open={x.id === openId} onPick={() => setOpenId(x.id === openId ? null : x.id)} lang={lang} today={today} />;
                  const c = x.c;
                  const a = c.a;
                  const line = agreementLine(contractFact(c), today, lang);
                  const what = contractWhat(a, lang);
                  return (
                    <tr key={a.id} class={`contract-row${a.id === openId ? ' is-open' : ''}`}>
                      <th scope="row">
                        <button type="button" class="contract-pick" aria-expanded={a.id === openId} onClick={() => setOpenId(a.id === openId ? null : a.id)}>
                          {a.name}
                        </button>
                        <span class="cell-sub">{a.kind === 'subscription' ? t.subscription : t.perProduction}</span>
                      </th>
                      <td>
                        {what}
                        {a.title ? <span class="cell-sub">{a.title}</span> : null}
                      </td>
                      <td>
                        {c.state === 'ended' ? (
                          <span class="muted">{c.endedBy === 'cancelled' ? t.cancelled : t.superseded}</span>
                        ) : (
                          <>
                            <span class={line.tone === 'warn' || line.tone === 'crit' ? `contract-${line.tone}` : undefined}>{line.tone === 'ok' ? (line.detail ?? line.label) : line.label}</span>
                            {line.tone !== 'ok' && line.detail ? <span class="cell-sub">{line.detail}</span> : null}
                            {c.period?.progress !== null && c.period?.progress !== undefined ? (
                              <span class="contract-bar" aria-hidden="true">
                                <i style={`width:${Math.round(c.period.progress * 100)}%`} />
                              </span>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td>{a.responsible ? <Avatar name={a.responsible.name} size="sm" /> : '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {open?.kind === 'hub' ? (
            <ContractDetail key={open.id} api={p.api} c={open.c} lang={lang} canEdit={canEdit} producers={producers} reload={load} />
          ) : open?.kind === 'monday' ? (
            <MondayDetail key={open.id} m={open.m} lang={lang} />
          ) : (
            <p class="muted contract-hint">{t.pick}</p>
          )}
        </div>
      ) : (
        <div class="empty">
          <strong>{t.none(t.filters[filter].toLowerCase())}</strong>
          <span class="muted">{t.noneHint}</span>
        </div>
      )}
    </div>
  );
}

function MondayRow(p: { x: Extract<Item, { kind: 'monday' }>; open: boolean; onPick: () => void; lang: Lang; today: string }) {
  const t = TX[p.lang];
  const { m } = p.x;
  const c = m.contract;
  const ongoing = /ongoing/i.test(c?.package ?? '');
  return (
    <tr class={`contract-row${p.open ? ' is-open' : ''}`}>
      <th scope="row">
        <button type="button" class="contract-pick" aria-expanded={p.open} onClick={p.onPick}>
          {m.client.name}
        </button>
        <span class="cell-sub">{t.fromMonday}</span>
      </th>
      <td>{mondayWhat(m, p.lang)}</td>
      <td>
        {p.x.superseded ? (
          <span class="muted">{t.superseded}</span>
        ) : (
          <>
            {c?.start || c?.end ? (
              <span>
                {c.start ? dayUi(c.start, p.lang) : '…'} – {ongoing ? t.untilNotice : c.end ? dayUi(c.end, p.lang) : '…'}
              </span>
            ) : ongoing ? (
              <span>{t.untilNotice}</span>
            ) : (
              <span class="muted">–</span>
            )}
            {p.x.state === 'active' && m.renewSoon && c?.end ? <span class="cell-sub contract-warn">{t.runsOut(dayUi(c.end, p.lang))}</span> : null}
          </>
        )}
      </td>
      <td>–</td>
    </tr>
  );
}

/** Monday's agreement as Monday had it: its columns and its files, opened in Drive once copied. */
function MondayDetail(p: { m: MondayAgreementRow; lang: Lang }) {
  const t = TX[p.lang];
  const { m } = p;
  const c = m.contract;
  const rows = (
    c
      ? [
          [t.mondayPkg, c.package],
          [t.mondayMrr, c.mrr ? kr(c.mrr) : null],
          [t.mondayValue, c.value ? kr(c.value) : null],
          [t.start, c.start ? dayUi(c.start, p.lang) : null],
          [t.end, /ongoing/i.test(c.package ?? '') ? t.untilNotice : c.end ? dayUi(c.end, p.lang) : null],
          [t.mondayNotice, c.terminationMonths ? t.noticeMonths(c.terminationMonths) : null]
        ]
      : []
  ).filter((r): r is [string, string] => Boolean(r[1]));
  return (
    <Block title={`${m.client.name} · ${t.mondayTitle}`}>
      <div class="stack contract-detail">
        <p class="contract-text muted">{t.mondayNote}</p>
        {rows.length ? (
          <section>
            <h3 class="contract-h">{t.included}</h3>
            <dl class="contract-dates">
              {rows.map(([k, v]) => (
                <Fragment key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </Fragment>
              ))}
            </dl>
          </section>
        ) : null}
        <section>
          <h3 class="contract-h">{t.files}</h3>
          {m.files.length ? (
            <ul class="contract-list">
              {m.files.map((f) => (
                <li key={f.id}>
                  {f.url ? (
                    <a href={f.url} target="_blank" rel="noopener noreferrer">
                      {f.name}
                    </a>
                  ) : (
                    <span>{f.name}</span>
                  )}{' '}
                  <span class="muted">{[f.at ? dayUi(f.at.slice(0, 10), p.lang) : null, f.state === 'waiting' ? t.fileWaiting : f.state === 'failed' ? t.fileFailed : null].filter(Boolean).join(' · ')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p class="contract-text muted">{t.noFiles}</p>
          )}
        </section>
        <p>
          <a href={`/clients/${m.client.id}`} onClick={(e) => (e.preventDefault(), navigate(`/clients/${m.client.id}`))}>
            {t.client}
          </a>
        </p>
      </div>
    </Block>
  );
}

function OutForSigning(p: { rows: OpenSigning[]; lang: Lang }) {
  const t = TX[p.lang];
  return (
    <Block title={`${t.out} (${p.rows.length})`} id="signing-out" tour="signing">
      <ul class="signing-out">
        {p.rows.map((r) => {
          const d = dayUi(r.sentAt.slice(0, 10), p.lang);
          return (
            <li key={r.id}>
              <a
                href={r.link}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(r.link);
                }}
              >
                {r.title}
              </a>{' '}
              <span class="pill pill--neutral">{t.kinds[r.kind]}</span>
              <span class="cell-sub">
                {r.sentBy ? t.sentBy(r.sentBy, d) : t.sentOn(d)} · {t.signedOf(r.done, r.total)}
                {r.waitingOn.length ? ` · ${t.waiting(r.waitingOn.map((w) => (w.viewed ? `${w.name} (${t.viewed})` : w.name)).join(', '))}` : ''}
              </span>
            </li>
          );
        })}
      </ul>
    </Block>
  );
}

function ContractDetail(p: { api: Api; c: ContractRow; lang: Lang; canEdit: boolean; producers: { id: string; name: string }[]; reload: () => Promise<void> }) {
  const t = TX[p.lang];
  const { a, period } = p.c;
  const [handing, setHanding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const projects = a.projects ?? [];
  const openPdf = async () => {
    setErr('');
    try {
      const f = await p.api.get<{ name: string; mimeType: string; data: string }>(`/api/agreements/${a.id}/signed-pdf`);
      const bytes = Uint8Array.from(atob(f.data), (ch) => ch.charCodeAt(0));
      window.open(URL.createObjectURL(new Blob([bytes], { type: f.mimeType })), '_blank', 'noopener');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setErr('');
    try {
      await p.api.putBytes(`/api/agreements/${a.id}/signed-pdf?name=${encodeURIComponent(file.name)}`, file);
      await p.reload();
      toast({ text: t.uploaded });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const s = a.summary;
  const nm = (o: { en: string; sv: string }) => (p.lang === 'sv' ? o.sv : o.en);
  const months = a.kind === 'project' ? (a.terms.periodMonths ?? 2) : a.terms.periodMonths;
  return (
    <Block title={`${a.name} · ${t.agreement(a.ref)}`}>
      <div class="stack contract-detail">
        <section>
          <h3 class="contract-h">{t.included}</h3>
          <ul class="contract-list">
            {a.kind === 'subscription' ? (
              <>
                <li>
                  {a.terms.packageKey}{s.pkg ? ` (${nm(s.pkg)})` : ''} · {kr(s.monthly)}
                  {t.perMonth}
                </li>
                <li>{months ? t.binding(months) : t.untilNotice}</li>
              </>
            ) : (
              <>
                {s.callOff.map((x) => (
                  <li key={x.key}>
                    {x.key} ({nm(x)}) · {kr(x.price)} {t.perProd}
                  </li>
                ))}
                {a.terms.minProductions === 0 ? <li>{t.frame}</li> : a.terms.minProductions && months ? <li>{t.minimum(a.terms.minProductions, months)}</li> : null}
              </>
            )}
          </ul>
        </section>
        {s.options.length ? (
          <section>
            <h3 class="contract-h">{t.options}</h3>
            <ul class="contract-list">
              {s.options.map((o) => (
                <li key={o.key}>
                  {nm(o)} · {o.from ? t.from : ''}
                  {kr(o.price)}
                  {o.unit === 'h' ? t.perHour : ''}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {a.terms.customContents ? (
          <section>
            <h3 class="contract-h">{t.contents}</h3>
            <p class="contract-text">{a.terms.customContents}</p>
          </section>
        ) : null}
        {a.terms.specialTerms ? (
          <section>
            <h3 class="contract-h">{t.special}</h3>
            <p class="contract-text">{a.terms.specialTerms}</p>
          </section>
        ) : null}
        <section>
          <h3 class="contract-h">{t.dates}</h3>
          <dl class="contract-dates">
            {a.signedOn ? (
              <>
                <dt>{t.signed}</dt>
                <dd>{dayUi(a.signedOn, p.lang)}</dd>
              </>
            ) : null}
            {period ? (
              <>
                <dt>{t.start}</dt>
                <dd>{dayUi(period.start, p.lang)}</dd>
                <dt>{t.end}</dt>
                <dd>{period.end ? dayUi(period.end, p.lang) : t.untilNotice}</dd>
                {period.noticeBy ? (
                  <>
                    <dt>{t.noticeBy}</dt>
                    <dd class={period.renewSoon ? 'contract-warn' : undefined}>{dayUi(period.noticeBy, p.lang)}</dd>
                  </>
                ) : null}
              </>
            ) : null}
          </dl>
        </section>
        {p.c.state === 'active' ? (
          <section>
            <h3 class="contract-h">{t.handover}</h3>
            {a.handoverTo ? (
              <p class="contract-text">
                {t.handedTo(a.handoverTo.name, dayUi(a.handoverTo.at.slice(0, 10), p.lang))}
                {a.client ? (
                  <>
                    {' · '}
                    <a href={`/clients/${a.client.id}`} onClick={(e) => (e.preventDefault(), navigate(`/clients/${a.client!.id}`))}>
                      {t.readSummary}
                    </a>
                  </>
                ) : null}
              </p>
            ) : (
              <div class="cluster">
                <span class="muted">{t.notHanded}</span>
                {p.canEdit ? (
                  <button type="button" class="btn" onClick={() => setHanding(true)}>
                    {t.handOver}
                  </button>
                ) : null}
              </div>
            )}
          </section>
        ) : null}
        <section>
          <h3 class="contract-h">
            {t.projects}
            {a.kind === 'project' && a.terms.minProductions ? ` · ${t.calledOff(projects.length, a.terms.minProductions)}` : ''}
          </h3>
          {projects.length ? (
            <ul class="contract-list">
              {projects.map((x) => (
                <li key={x.id}>
                  <a href={`/projects/${x.id}`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${x.id}`))}>
                    {x.name}
                  </a>{' '}
                  <span class="muted">{x.deliveryMonth}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p class="contract-text muted">{t.noProjects}</p>
          )}
        </section>
        <section>
          <h3 class="contract-h">{t.signedPdf}</h3>
          <div class="cluster">
            {a.document ? (
              <button type="button" class="btn" onClick={() => void openPdf()}>
                {t.openPdf}
              </button>
            ) : (
              <span class="muted">{t.noPdf}</span>
            )}
            {p.canEdit && a.status === 'signed' ? (
              <label class={`btn btn-ghost${busy ? ' is-busy' : ''}`}>
                {busy ? t.uploading : a.document ? t.replacePdf : t.uploadPdf}
                <input type="file" accept="application/pdf" class="sr-only" disabled={busy} onChange={(e) => void upload((e.target as HTMLInputElement).files?.[0])} />
              </label>
            ) : null}
          </div>
          {err ? (
            <p class="field-error" role="alert">
              {err}
            </p>
          ) : null}
        </section>
        <div class="cluster">
          <a class="btn" href={`/agreements/${a.id}/contract`} target="_blank" rel="noopener">
            {t.open}
          </a>
          {a.client ? (
            <a class="btn btn-ghost" href={`/clients/${a.client.id}`} onClick={(e) => (e.preventDefault(), navigate(`/clients/${a.client!.id}`))}>
              {t.client}
            </a>
          ) : null}
        </div>
      </div>
      {handing ? (
        <HandoverDialog
          api={p.api}
          a={a}
          producers={p.producers}
          defaultProducer={a.responsible?.id ?? null}
          onClose={() => setHanding(false)}
          onDone={async (r) => {
            setHanding(false);
            await p.reload();
            if (r) toast({ text: handoverToast(p.lang, r) });
          }}
        />
      ) : null}
    </Block>
  );
}
